import { api } from '@/lib/api.js';

// MV-02 2.3 (Ship 2): market state ladder over submission_markets.

export async function listDealMarkets(dealId) {
  return api.get(`/api/tracon/deals/${dealId}/markets?_t=${Date.now()}`);
}

// "This was a clearance, not a submission." A correction on an existing row:
// it moves no state and takes the row out of the nudge clock.
export async function setMarketClearing(marketId, clearing) {
  return api.patch(`/api/tracon/markets/${marketId}/clearing`, { clearing });
}

export async function setMarketState(marketId, body) {
  return api.patch(`/api/tracon/markets/${marketId}/state`, body);
}

// Log a submission the broker made outside RTQ dispatch (WALKTHROUGH fix 4).
export async function createManualMarket(dealId, body) {
  return api.post(`/api/tracon/deals/${dealId}/markets`, body);
}

// Record a carrier answer that arrived through the absence of the option: the
// risk was not selectable in their system, so there was no submission at all.
// Lands on the same ladder as a replied decline, with decline_basis 'known' and
// no channel, sender or send date.
export async function recordKnownDecline(dealId, body) {
  return api.post(`/api/tracon/deals/${dealId}/markets/known-decline`, body);
}

// Active carriers for the "Log a submission" picker.
//
// With a dealId each carrier carries { signal, reasons, clearance } and the list
// comes back ranked most-likely first (CC PROMPT 23 Part C). EVERY CARRIER IS
// RETURNED and every one stays selectable: the signal sorts and annotates, it
// never gates. Without a dealId this is the flat list it always was.
export async function listCarrierOptions(dealId) {
  const q = dealId ? `dealId=${encodeURIComponent(dealId)}&` : '';
  return api.get(`/api/tracon/markets/carrier-options?${q}_t=${Date.now()}`);
}

// Quick-log a quote inline on a sent market row (Ship 2.2): mints a real
// Quotation as-applied-for and flips the row to quoted. body = { premium } for a
// single-aircraft deal, or { aircraft_premiums: [{tail_number_id, premium}] }
// for a multi-aircraft deal (A4).
export async function quickLogQuote(dealId, marketId, body) {
  return api.post(`/api/tracon/deals/${dealId}/markets/${marketId}/quick-log`, body);
}

export async function acceptMarketSuggestion(marketId) {
  return api.post(`/api/tracon/markets/${marketId}/suggestion/accept`, {});
}

export async function dismissMarketSuggestion(marketId) {
  return api.post(`/api/tracon/markets/${marketId}/suggestion/dismiss`, {});
}

// Blocked-market registry (item 4): account-level rollup.
export async function listBlockedMarkets(accountId) {
  return api.get(`/api/tracon/accounts/${accountId}/blocked-markets?_t=${Date.now()}`);
}

// Portal copy-sheet (item 7): config-driven field maps, mark-submitted opens the row.
export async function getPortalSheet(dealId, carrierSlug) {
  return api.get(`/api/tracon/deals/${dealId}/portal-sheet/${carrierSlug}?_t=${Date.now()}`);
}

export async function markPortalSubmitted(dealId, carrierSlug, body = {}) {
  return api.post(`/api/tracon/deals/${dealId}/portal-sheet/${carrierSlug}/mark-submitted`, body);
}

// Nudge timers (item 3): due-ness derived server-side; draft then broker sends.
export async function listFollowUpsDue() {
  return api.get(`/api/tracon/markets/follow-ups?_t=${Date.now()}`);
}

export async function getNudgeDraft(marketId) {
  return api.get(`/api/tracon/markets/${marketId}/nudge-draft`);
}

export async function sendNudge(marketId, body) {
  return api.post(`/api/tracon/markets/${marketId}/nudge-send`, body, { timeoutMs: 60_000 });
}

// A NO-OP STATE CHANGE IS STILL AN EVENT (187 addendum §2). Records that the
// carrier referred the row, whether or not the state moves — the old control
// skipped setMarketState entirely when the row was already `working`, so the
// referral left nothing in state_history at all.
export function recordMarketReferral(marketId, payload) {
  return api.post(`/api/tracon/markets/${encodeURIComponent(marketId)}/referral`, payload || {});
}
